// The /diff endpoints send the ChangeSet as plain JSON arrays (ReadonlyMaps
// aren't JSON-serializable). Core consumers like `buildLookup` iterate it as
// Maps (`for (const [id, m] of cs.modules)`), so the wire form must be revived
// into the Map-based core ChangeSet before it reaches DiffBoard / DiffProvider.

import type {
  ChangeSet,
  SurfaceDiff,
  FieldDiff,
  InterfaceDiff,
  ModuleDiff,
  ModuleId,
  Name,
  ProcessDiff,
  Status,
  SubprocessDiff,
  ViewDiff,
} from "@archlang/engine";

interface WFieldDiff {
  status: string;
  from?: unknown;
  to?: unknown;
}
interface WKeyed {
  key: string;
  diff: WFieldDiff;
}
interface WIface {
  name: string;
  status: string;
  pathChange?: { from: string; to: string };
  typeChange?: { from: string; to: string };
  fields: WKeyed[];
  sourceFile?: string;
}
interface WSurface {
  name: string;
  status: string;
  fields: WKeyed[];
  aspects: WKeyed[];
  interfaces: WIface[];
  surfaces: WSurface[];
}
interface WModule {
  id: string;
  name: string;
  status: string;
  nameChange?: { from: string; to: string };
  typeChange?: { from: string; to: string };
  fields: WKeyed[];
  aspects: WKeyed[];
  interfaces: WIface[];
  surfaces: WSurface[];
  children: WModule[];
  sourceFile?: string;
}
interface WProc {
  id: string;
  name: string;
  status: string;
  ownerPath?: string[];
  steps: { added: unknown[]; removed: unknown[]; modified: unknown[]; reordered: boolean };
  sourceFile?: string;
}
export interface WireChangeSet {
  modules: WModule[];
  processes: WProc[];
  subprocesses: WProc[];
  views: { id: string; status: string; sourceFile?: string }[];
}

const fieldMap = (rows: WKeyed[] = []): Map<string, FieldDiff> =>
  new Map(rows.map((r) => [r.key, { status: r.diff.status as Status, from: r.diff.from, to: r.diff.to } as FieldDiff]));

function reviveInterface(w: WIface): InterfaceDiff {
  return {
    name: w.name as Name,
    status: w.status as Status,
    pathChange: w.pathChange,
    typeChange: w.typeChange,
    fields: fieldMap(w.fields),
    sourceFile: w.sourceFile,
  };
}

function reviveSurface(w: WSurface): SurfaceDiff {
  return {
    name: w.name as Name,
    status: w.status as Status,
    fields: fieldMap(w.fields),
    aspects: fieldMap(w.aspects),
    interfaces: new Map((w.interfaces ?? []).map((i) => [i.name, reviveInterface(i)])),
    surfaces: new Map((w.surfaces ?? []).map((f) => [f.name, reviveSurface(f)])),
  };
}

function reviveModule(w: WModule): ModuleDiff {
  return {
    id: w.id as ModuleId,
    name: w.name as Name,
    status: w.status as Status,
    nameChange: w.nameChange,
    typeChange: w.typeChange,
    fields: fieldMap(w.fields),
    aspects: fieldMap(w.aspects),
    interfaces: new Map((w.interfaces ?? []).map((i) => [i.name, reviveInterface(i)])),
    surfaces: new Map((w.surfaces ?? []).map((f) => [f.name, reviveSurface(f)])),
    children: new Map((w.children ?? []).map((c) => [c.id as ModuleId, reviveModule(c)])),
    sourceFile: w.sourceFile,
  };
}

function reviveProcess(w: WProc): ProcessDiff {
  return {
    id: w.id as ProcessDiff["id"],
    name: w.name as Name,
    status: w.status as Status,
    ownerPath: w.ownerPath,
    steps: w.steps as ProcessDiff["steps"],
    sourceFile: w.sourceFile,
  };
}

export function deserializeChangeSet(w: WireChangeSet): ChangeSet {
  return {
    modules: new Map((w.modules ?? []).map((m) => [m.id as ModuleId, reviveModule(m)])),
    processes: new Map((w.processes ?? []).map((p) => [p.id as ProcessDiff["id"], reviveProcess(p)])),
    subprocesses: new Map(
      (w.subprocesses ?? []).map((p) => [p.id, reviveProcess(p) as unknown as SubprocessDiff]),
    ),
    views: new Map(
      (w.views ?? []).map((v) => [
        v.id,
        { id: v.id, status: v.status as Status, fields: new Map(), sourceFile: v.sourceFile } as ViewDiff,
      ]),
    ),
  };
}
